import React, { useSyncExternalStore } from 'react'
import { Slide, toast } from 'react-toastify'
import BulkProgressToast from '../components/BulkProgressToast/BulkProgressToast'

export interface BulkDeleteItem {
    id: string
    name: string
}

export interface BulkDeleteResult {
    deleted: BulkDeleteItem[]
    failed: BulkDeleteItem[]
}

interface BulkDeleteOptions {
    items: BulkDeleteItem[]
    // Must resolve to a truthy value when the item has been deleted. Rejections are caught
    // and counted as failures, so a single error never stops the remaining deletions.
    deleteItem: (item: BulkDeleteItem) => Promise<unknown>
    title: string
    entityLabel: { singular: string; plural: string }
}

// --- Running state, shared across pages so a deletion started in a folder keeps
// --- blocking new bulk deletions (and page reloads) even after the user navigates away.

let isRunning = false
const listeners = new Set<() => void>()

const setRunning = (value: boolean) => {
    isRunning = value
    listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

export const useIsBulkDeleteRunning = (): boolean => useSyncExternalStore(subscribe, () => isRunning)

// Triggers the browser's native "leave site?" dialog on reload, tab close or external navigation.
const preventPageUnload = (event: BeforeUnloadEvent) => {
    event.preventDefault()
    event.returnValue = ''
    return ''
}

const toastBaseOptions = {
    position: 'bottom-right' as const,
    transition: Slide,
}

export const runBulkDelete = async ({
    items,
    deleteItem,
    title,
    entityLabel,
}: BulkDeleteOptions): Promise<BulkDeleteResult> => {
    const result: BulkDeleteResult = { deleted: [], failed: [] }
    if (isRunning || items.length === 0) return result

    setRunning(true)
    window.addEventListener('beforeunload', preventPageUnload)

    const total = items.length
    const renderProgress = (processed: number, currentItemName?: string) => (
        <BulkProgressToast
            title={title}
            processed={processed}
            total={total}
            failed={result.failed.length}
            currentItemName={currentItemName}
        />
    )

    const toastId = toast.loading(renderProgress(0, items[0].name), toastBaseOptions)

    try {
        // Requests are sent one after the other, against the existing single-delete endpoint.
        for (let index = 0; index < total; index++) {
            const item = items[index]
            const succeeded = await deleteItem(item)
                .then((response) => !!response)
                .catch(() => false)

            if (succeeded) result.deleted.push(item)
            else result.failed.push(item)

            toast.update(toastId, { render: renderProgress(index + 1, items[index + 1]?.name) })
        }
    } finally {
        window.removeEventListener('beforeunload', preventPageUnload)
        setRunning(false)
    }

    const deletedCount = result.deleted.length
    const failedCount = result.failed.length
    const label = (count: number) => (count === 1 ? entityLabel.singular : entityLabel.plural)

    let summary: string
    let type: 'success' | 'warning' | 'error'
    if (failedCount === 0) {
        type = 'success'
        summary = `${deletedCount} ${label(deletedCount)} ${deletedCount === 1 ? 'eliminato' : 'eliminati'} con successo.`
    } else if (deletedCount === 0) {
        type = 'error'
        summary = `Non è stato possibile eliminare ${failedCount === 1 ? "l'elemento selezionato" : `i ${failedCount} elementi selezionati`}. Riprova più tardi.`
    } else {
        type = 'warning'
        summary = `${deletedCount} di ${total} ${label(total)} ${deletedCount === 1 ? 'eliminato' : 'eliminati'}. ${failedCount} ${failedCount === 1 ? 'non è stato eliminato' : 'non sono stati eliminati'}: riprova più tardi.`
    }

    toast.update(toastId, {
        ...toastBaseOptions,
        render: summary,
        type,
        isLoading: false,
        autoClose: failedCount > 0 ? 8000 : 5000,
        hideProgressBar: true,
        closeOnClick: true,
        closeButton: true,
        draggable: true,
        pauseOnHover: true,
    })

    return result
}
